import AtmosphericHero from '@/components/marketing/AtmosphericHero'
import FaqAccordion, { FaqItem } from '@/components/marketing/FaqAccordion'
import ScrollReveal from '@/components/marketing/ScrollReveal'
import type { SharedMarketingProps } from '@/types/marketing'

interface PlaylistPitchingPageProps extends SharedMarketingProps {
  hero: {
    eyebrow: string
    title: string
    body: string
    /** Deep link to the workspace pitch form — unauthenticated visitors go
     *  through the Account Hub login and land on the form after the OTP. */
    cta?: { label: string; href: string }
  }
  steps: { step: string; title: string; body: string }[]
  deadlines: { value: string; unit: string; label: string }[]
  included: { title: string; body: string }[]
  prepare: { title: string; note: string; items: string[] }
  faq_items: FaqItem[]
  cta: {
    title: string
    body: string
    actions: { label: string; href: string }[]
    existing_user?: { prompt: string; label: string; href: string }
  }
}

export default function PlaylistPitching({
  hero,
  steps,
  deadlines,
  included,
  prepare,
  faq_items,
  cta,
}: PlaylistPitchingPageProps) {
  return (
    <>
      <AtmosphericHero minHeight="56vh">
        <div className="mx-auto max-w-3xl text-center space-y-10">
          <ScrollReveal>
            <p className="text-sm font-medium uppercase tracking-[0.3em] text-[#ff6a1a]">
              {hero.eyebrow}
            </p>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <h1 className="hero-headline" style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)' }}>
              {hero.title}
            </h1>
          </ScrollReveal>
          <ScrollReveal delay={0.15}>
            <p className="hero-body mx-auto">{hero.body}</p>
          </ScrollReveal>
          {hero.cta && (
            <ScrollReveal delay={0.2}>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <a href={hero.cta.href} className="marketing-button-primary">
                  {hero.cta.label}
                </a>
                <span className="text-sm text-white/40">
                  Sign in with your wavbee email — we&rsquo;ll take you straight to the form.
                </span>
              </div>
            </ScrollReveal>
          )}
        </div>
      </AtmosphericHero>

      {/* The deadlines that matter — big-number strip */}
      <section className="container mx-auto px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <h2
              className="marketing-section-title text-center text-white"
              style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}
            >
              The deadlines that decide everything
            </h2>
          </ScrollReveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {deadlines.map((deadline, index) => (
              <ScrollReveal key={deadline.label} delay={index * 0.08}>
                <div className="group relative h-full overflow-hidden rounded-[1.75rem] border border-white/[0.08] bg-gradient-to-b from-white/[0.06] to-white/[0.02] p-7 text-center transition-all duration-200 hover:-translate-y-1 hover:border-[#ff6a1a]/30 hover:shadow-[0_20px_50px_rgba(255,106,26,0.08)]">
                  <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-[#ff6a1a]/[0.06] blur-2xl transition-all duration-300 group-hover:bg-[#ff6a1a]/[0.14]" />
                  <p
                    className="text-6xl font-bold text-[#ff6a1a]"
                    style={{ fontFamily: 'var(--wavbee-font-display)' }}
                  >
                    {deadline.value}
                  </p>
                  <p className="mt-1 text-xs font-medium uppercase tracking-wider text-[#ff6a1a]/60">
                    {deadline.unit}
                  </p>
                  <p className="mt-4 text-sm leading-relaxed text-white/60">{deadline.label}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
          <ScrollReveal delay={0.3}>
            <p className="mt-8 text-center text-sm text-white/40">
              Pitching is for upcoming releases only — once a track is live, the window has closed.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* How it works — alternating timeline */}
      <section className="container mx-auto px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <h2
              className="marketing-section-title text-center text-white"
              style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}
            >
              How pitch requests work
            </h2>
          </ScrollReveal>
          <div className="relative mt-12">
            <div className="absolute left-1/2 top-0 hidden h-full w-px bg-gradient-to-b from-[#ff6a1a]/40 via-[#ff6a1a]/20 to-transparent lg:block" />

            <div className="space-y-5 lg:space-y-10">
              {steps.map((step, index) => (
                <ScrollReveal key={step.step} delay={index * 0.1}>
                  {/* Desktop: alternating timeline */}
                  <div className={`hidden lg:flex items-start gap-12 ${index % 2 === 1 ? 'flex-row-reverse' : ''}`}>
                    <div className="flex-1" />
                    <div className="relative z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-[#ff6a1a]/30 bg-[#0a0a0a]">
                      <span className="text-sm font-bold text-[#ff6a1a]">{step.step}</span>
                    </div>
                    <div className="flex-1 rounded-[1.75rem] border border-white/[0.08] bg-gradient-to-b from-white/[0.06] to-white/[0.02] p-6 transition-all duration-200 hover:-translate-y-1 hover:border-white/[0.16] hover:shadow-[0_20px_50px_rgba(0,0,0,0.4)]">
                      <h3 className="text-lg font-semibold text-white" style={{ fontFamily: 'var(--wavbee-font-display)' }}>
                        {step.title}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-white/60">{step.body}</p>
                    </div>
                  </div>

                  {/* Mobile: stacked cards */}
                  <div className="flex items-start gap-4 lg:hidden">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#ff6a1a]/30 bg-[#ff6a1a]/10">
                      <span className="text-xs font-bold text-[#ff6a1a]">{step.step}</span>
                    </div>
                    <div className="flex-1 rounded-2xl border border-white/[0.08] bg-gradient-to-b from-white/[0.06] to-white/[0.02] p-5">
                      <h3 className="text-base font-semibold text-white">{step.title}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-white/60">{step.body}</p>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What's included */}
      <section className="container mx-auto px-6 py-20">
        <ScrollReveal>
          <h2
            className="marketing-section-title mx-auto max-w-4xl text-center text-white"
            style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}
          >
            What wavbee&apos;s pitching support includes
          </h2>
        </ScrollReveal>
        <div className="mx-auto mt-10 grid max-w-5xl gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {included.map((item, index) => (
            <ScrollReveal key={item.title} delay={index * 0.08}>
              <div className="group h-full rounded-[1.75rem] border border-white/[0.08] bg-gradient-to-b from-white/[0.06] to-white/[0.02] p-7 transition-all duration-200 hover:-translate-y-1 hover:border-[#ff6a1a]/20 hover:shadow-[0_20px_50px_rgba(0,0,0,0.4)]">
                <div className="mb-4 h-1 w-8 rounded-full bg-[#ff6a1a]/30 transition-all duration-200 group-hover:w-12 group-hover:bg-[#ff6a1a]/60" />
                <h3
                  className="text-base font-semibold text-white"
                  style={{ fontFamily: 'var(--wavbee-font-display)' }}
                >
                  {item.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-white/50 transition-colors duration-200 group-hover:text-white/70">
                  {item.body}
                </p>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* What to prepare */}
      <section className="container mx-auto px-6 py-20">
        <ScrollReveal>
          <div className="mx-auto max-w-3xl overflow-hidden rounded-[1.75rem] border border-[#ff6a1a]/20 bg-gradient-to-br from-[#ff6a1a]/[0.08] to-transparent p-10">
            <h2
              className="text-xl font-bold text-white"
              style={{ fontFamily: 'var(--wavbee-font-display)' }}
            >
              {prepare.title}
            </h2>
            <ul className="mt-6 space-y-4">
              {prepare.items.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-white/70">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#ff6a1a]/15 text-xs font-bold text-[#ff6a1a]">
                    ✓
                  </span>
                  {item}
                </li>
              ))}
            </ul>
            <p className="mt-8 border-t border-white/[0.08] pt-6 text-sm italic text-white/50">{prepare.note}</p>
          </div>
        </ScrollReveal>
      </section>

      {/* FAQ */}
      <section className="container mx-auto px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <h2
              className="marketing-section-title text-center text-white"
              style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}
            >
              Pitching, answered
            </h2>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <div className="mt-10">
              <FaqAccordion items={faq_items} />
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA */}
      <section className="container mx-auto px-6 py-24">
        <ScrollReveal>
          <div className="marketing-final-cta mx-auto max-w-2xl rounded-[1.75rem] border border-white/[0.08] p-10 text-center">
            <h2
              className="text-2xl font-bold text-white"
              style={{ fontFamily: 'var(--wavbee-font-display)' }}
            >
              {cta.title}
            </h2>
            <p className="mt-3 text-white/60">{cta.body}</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              {cta.actions.map((action, index) => (
                <a
                  key={action.label}
                  href={action.href}
                  className={index === 0 ? 'marketing-button-primary' : 'marketing-button-secondary'}
                >
                  {action.label}
                </a>
              ))}
            </div>
            {cta.existing_user && (
              <p className="mt-6 border-t border-white/[0.08] pt-5 text-sm text-white/50">
                {cta.existing_user.prompt}{' '}
                <a
                  href={cta.existing_user.href}
                  className="font-medium text-[#ff6a1a] transition-colors hover:text-[#ff8c4a]"
                >
                  {cta.existing_user.label} →
                </a>
              </p>
            )}
          </div>
        </ScrollReveal>
      </section>
    </>
  )
}
